import * as UI from 'core/ui';
import { BackActionButton, Content, Filler, Footer, Header, PosScreen, ThemeManager } from 'pos/ui';
import { InvoiceInListDTO, PrintTemplateType, Res, SerieInListDTO } from 'core/api';
import { SaleModuleNextAction } from '../helpers/SaleModuleNextAction';
import { InvoiceDTO, PrintPreviewDTO } from 'core/api';
import { Command } from 'pos/common';
import { Events, LocalStorage, Result, SessionProvider } from 'core/infrastructure';
import { InvoiceFacade, PrintFacade } from 'pos/facades';
import * as InvoiceStore from './InvoiceStore';
import { Message, Modal } from 'core/ui-next/modal';
import { Toaster } from 'core/ui-next';
import React from 'react';

export type Props = {
	businessDay: Date;
	initialAllSeries: SerieInListDTO[];
} & Modal.Actions<SaleModuleNextAction>;

type ShowInvoicesLayoutProps = Pick<Props, 'cancel'> & {
	content: React.ReactNode;
	footer?: React.ReactNode;
};

export function ShowInvoices({ cancel, content, footer }: ShowInvoicesLayoutProps) {

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='INVOICES' size={'full-screen'} useKeyPad={false}>
		<Header title={Res.Web.SHOW_INVOICES()} onClose={cancel} />
		<Content>
			{content}
		</Content>
		<Footer>
			<BackActionButton action={onCancel} />
			{(ThemeManager.isPhone() || ThemeManager.isTablet()) && (
				<>
					<Filler />
					{/* Botones de comandera y tablet en la parte inferior de la pantalla */}
					{footer}
				</>
			)}
		</Footer>
	</PosScreen>;
}

// Custom hook equivalente al antiguo ShowInvoices.onComponentDidMount:
// suscribe los eventos de dominio y dispara la carga inicial una sola vez.
// Usa un ref para que las suscripciones no dependan de la identidad de `reload`
// (que cambia en cada render al depender del estado del componente hijo).
export function useInvoiceReload(reload: () => Promise<void>) {

	const reloadRef = React.useRef(reload);
	React.useEffect(() => {
		reloadRef.current = reload;
	});

	React.useEffect(() => {
		const onOperationFailed = async ({ msg }: { msg: string }) => {
			await Message.show(msg);
			await reloadRef.current();
		};
		const onInvoiceChanged = async () => await reloadRef.current();

		Events.on('pos:invoice:operation-failed', onOperationFailed);
		Events.on('pos:invoice:changed', onInvoiceChanged);

		void reloadRef.current();

		return () => {
			Events.off('pos:invoice:operation-failed', onOperationFailed);
			Events.off('pos:invoice:changed', onInvoiceChanged);
		};
	}, []);
}

export type InvoiceInListModel = InvoiceInListDTO & { SerieAndNumber: string };

export type Selection = {
	invoice: InvoiceDTO,
	preview: PrintPreviewDTO
}

export const toModel = (invoices: InvoiceInListDTO[]) => invoices.sortBy(x => x.Id, 'desc').map(item => ({
	...item,
	SerieAndNumber: UI.Format.asDocumentNumber(item.Serie, item.Number),
}));

const GET_SHOW_ALL_INVOICES_KEY = (posId: number) => `ShowInvoicesScreen.showAllInvoices_${posId}`;

export function toggleShowAllInvoices() {
	const mustShowAll = !mustShowAllInvoices();
	LocalStorage.setItem(GET_SHOW_ALL_INVOICES_KEY(SessionProvider.getCurrent().PosId), mustShowAll.toString());
}

export function mustShowAllInvoices() {
	return LocalStorage.getItem(GET_SHOW_ALL_INVOICES_KEY(SessionProvider.getCurrent().PosId)) === 'true' ? true : false;
}

export async function updateInvoices(newInvoices: InvoiceInListDTO[], currentSelection: Nullable<Selection>): Promise<{ invoices: InvoiceInListModel[], selection: Nullable<Selection> }> {

	const invoices = toModel(newInvoices);
	const invoiceIdToSelect = getInvoiceIdToSelect(currentSelection?.invoice ?? null, invoices);

	const selection = await select(invoiceIdToSelect);

	return { invoices, selection };
}

export async function select(invoiceId: Nullable<number>): Promise<Nullable<Selection>> {
	if (!invoiceId)
		return null;

	// El preview puede lanzar una excepción de dominio si no se han configurado correctamente los templates de TicketBAI o Verifactu
	const preview = await PrintFacade.getInvoicePreviews(invoiceId)
		.then(previews => previews[0])
		.catch(err => {
			Toaster.error(err.message);
			return { EscPosNumberOfColumns: 42, EscPosPreview: '', RdlBase64Images: [], TemplateType: PrintTemplateType.EscPos } satisfies PrintPreviewDTO;
		});
	return {
		invoice: await InvoiceFacade.getById(invoiceId),
		preview
	};
}

export function getInvoiceIdToSelect(currentSelection: Nullable<InvoiceDTO>, newInvoices: InvoiceInListModel[]): Nullable<number> {
	if (currentSelection && newInvoices.some(x => x.Id === currentSelection!.Id))
		return currentSelection!.Id;

	return newInvoices.sortBy(x => x.CreationDate).last()?.Id || 0;
}

export async function reopenInvoice(selection: Nullable<Selection>): Promise<Result<SaleModuleNextAction>> {

	if (!selection)
		return Result.error();

	const result = await Command.execute('reopenInvoice', selection.invoice);
	if (result.ok)
		return Result.ok({ type: 'loadTicket', ticketId: result.value.reopenedTicketId });

	return Result.error();
}

export async function createTotalRefund(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	return Command.execute('createInvoiceRefund', selection.invoice);
}

export async function createPartialRefund(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	return Command.execute('createInvoicePartialRefund', selection.invoice);
}

export async function setTips(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	return Command.execute('updateInvoiceTips', selection.invoice);
}


export async function showPreview(title: string, actionImage: string | SvgIcon, selection: Nullable<Selection>) {
	if (!selection)
		return Result.error();

	return Command.execute('showInvoicePreview', selection.invoice, title, actionImage);
}


export async function sendEmail(selection: Nullable<Selection>) {
	if (!selection)
		return Result.error();

	return Command.execute('sendInvoiceByEmail', selection.invoice);
}

export async function printInvoice(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	return Command.execute('printInvoice', selection.invoice);
}

export async function convertToStandard(selection: Nullable<Selection>) {
	if (!selection)
		return Result.error();

	return Command.execute('convertInvoiceToStandard', selection.invoice);
}

export async function changePayment(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	const ticketGlobalId = await InvoiceFacade.getTicketGlobalIdByInvoiceId(selection.invoice.Id);

	return Command.execute('changeInvoicePayments', {
		getDocument: () => {

			return {
				invoiceId: selection.invoice.Id,
				ticketGlobalId,
				invoiceType: selection.invoice.Type,
				posId: selection.invoice.PosId,
				customer: selection.invoice.Customer,
				pendingAmount: selection.invoice.ClosingAmountToPay
			};
		},
		changePayment: params => InvoiceStore.changePayment(selection.invoice.Id, params)
	});
}

export async function printCoupons(selection: Nullable<Selection>) {
	if (!selection)
		return Result.error();

	return Command.execute('printInvoiceCoupons', selection.invoice);
}

export async function assignOrUpdateCustomer(selection: Nullable<Selection>) {

	if (!selection)
		return Result.error();

	return Command.execute('updateInvoiceCustomer', selection.invoice);
}

export async function printTicketGift(selection: Nullable<Selection>): Promise<Result> {
	if (!selection)
		return Result.error();
	return Command.execute('printInvoiceTicketGift', selection.invoice);
}
